import { useCallback, useMemo, useState, useTransition } from "react";
import { useTypedSearchParams } from "react-router-typesafe-routes";
import type { EvidenceListFilters, SortDirection } from "~/features/evidences/types";
import { v2Route } from "~/v2/routes";
import { EvidencesPresenter } from "./evidences.presenter";

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_DIRECTION: SortDirection = "desc";

const splitIds = (value: string | undefined): string[] => (value ? value.split(",").filter((id) => id.length > 0) : []);

export function EvidencesContainer() {
  const [params, setParams] = useTypedSearchParams(v2Route.evidences);
  const [isPending, startTransition] = useTransition();

  const direction: SortDirection = params.sortOrderDirection ?? DEFAULT_DIRECTION;
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;

  const filters: EvidenceListFilters = useMemo(
    () => ({ referenceIds: splitIds(params.referenceId), userId: params.userId }),
    [params.referenceId, params.userId],
  );

  const onApplyFilters = useCallback(
    (next: EvidenceListFilters) => {
      setParams((prev) => ({
        ...prev,
        referenceId: next.referenceIds.length ? next.referenceIds.join(",") : undefined,
        userId: next.userId || undefined,
        page: undefined,
      }));
    },
    [setParams],
  );

  const onSortingChange = useCallback(
    (nextDirection: SortDirection) => {
      startTransition(() => {
        setParams((prev) => ({
          ...prev,
          sortOrderDirection: nextDirection === DEFAULT_DIRECTION ? undefined : nextDirection,
          page: undefined,
        }));
      });
    },
    [setParams],
  );

  const onPageIndexChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, page: next === 0 ? undefined : next }));
      });
    },
    [setParams],
  );

  const onPageSizeChange = useCallback(
    (next: number) => {
      startTransition(() => {
        setParams((prev) => ({ ...prev, pageSize: next === DEFAULT_PAGE_SIZE ? undefined : next, page: undefined }));
      });
    },
    [setParams],
  );

  const selectedEvidence = params.selectedEvidence ?? null;
  const onSelect = useCallback((id: string) => setParams((prev) => ({ ...prev, selectedEvidence: id })), [setParams]);
  const onCloseSelected = useCallback(
    () => setParams((prev) => ({ ...prev, selectedEvidence: undefined })),
    [setParams],
  );

  return (
    <EvidencesPresenter
      filters={filters}
      onApplyFilters={onApplyFilters}
      direction={direction}
      onSortingChange={onSortingChange}
      pageIndex={pageIndex}
      onPageIndexChange={onPageIndexChange}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      isPending={isPending}
      onSelect={onSelect}
      selectedEvidence={selectedEvidence}
      onCloseSelected={onCloseSelected}
    />
  );
}
